import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { 
  safeSetObject, 
  safeGetObject, 
  safeLocalStorageRemoveItem,
  isLocalStorageAvailable,
  validateTripData,
  getErrorMessage,
  monitorStorageUsage
} from '../utils/localStorage';

// Type definitions
interface User {
  id: string;
  googleId: string;
  email: string;
  name: string;
  picture: string;
  preferences: {
    travelStyle: 'luxury' | 'budget' | 'mid-range';
    interests: string[];
    groupType: 'solo' | 'couple' | 'family' | 'friends';
  };
}

interface Activity {
  time: string;
  activity: string;
}

interface DayPlan {
  id: number;
  day: string;
  activities: Activity[];
}

interface Trip {
  _id: string;
  userId: string;
  destination: string;
  dates: string;
  status: 'planning' | 'active' | 'completed';
  currentDay: string | null;
  activities: DayPlan[];
  conversationSummary: string;
  messages: Array<{
    role: 'user' | 'assistant';
    content: string;
    timestamp: Date;
  }>;
  createdAt: Date;
  updatedAt: Date;
}

interface UserContextType {
  user: User | null;
  currentTrip: Trip | null;
  loading: boolean;
  storageError: string | null;
  login: (userData: User) => void;
  logout: () => void;
  setTrip: (tripData: Trip) => void;
  updateTrip: (updates: Partial<Trip>) => void;
  createNewTrip: () => Promise<Trip>;
  clearStorageError: () => void;
  flushPendingSaves: () => void; // For testing: force immediate save of pending data
}

interface UserProviderProps {
  children: ReactNode;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [currentTrip, setCurrentTrip] = useState<Trip | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [storageError, setStorageError] = useState<string | null>(null);
  
  // Performance optimization: refs for debounced saves
  const pendingTripRef = useRef<Trip | null>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const initializeApp = async () => {
      // Check if localStorage is available
      if (!isLocalStorageAvailable()) {
        setStorageError('Local storage is disabled. Trip data will not be saved.');
        setLoading(false);
        return;
      }

      // TEMPORARY: Auto-login with test user for development
      const testUser: User = {
        id: 'test-user-id',
        googleId: 'test-google-id',
        email: 'test@example.com',
        name: 'Test User',
        picture: '',
        preferences: {
          travelStyle: 'luxury',
          interests: ['culture', 'food'],
          groupType: 'couple'
        }
      };

      // Load user with error handling
      const userResult = safeGetObject<User>('user');
      if (userResult.success && userResult.data) {
        setUser(userResult.data);
      } else {
        // TEMPORARY: Auto-login for testing
        setUser(testUser);
        const saveUserResult = safeSetObject('user', testUser);
        if (!saveUserResult.success) {
          setStorageError(getErrorMessage(saveUserResult.error || 'STORAGE_ERROR'));
        }
      }
      
      // Load trip with error handling and validation
      const tripResult = safeGetObject<Trip>('currentTrip');
      if (tripResult.success && tripResult.data) {
        if (validateTripData(tripResult.data)) {
          // Convert date strings back to Date objects
          const tripData = tripResult.data;
          tripData.createdAt = new Date(tripData.createdAt);
          tripData.updatedAt = new Date(tripData.updatedAt);
          tripData.messages = tripData.messages.map((msg: { role: 'user' | 'assistant'; content: string; timestamp: string | Date }) => ({
            ...msg,
            timestamp: new Date(msg.timestamp)
          }));
          setCurrentTrip(tripData);
        } else {
          // Corrupted trip data - clear and start fresh
          setStorageError('Saved trip data was corrupted and has been cleared.');
          safeLocalStorageRemoveItem('currentTrip');
        }
      } else if (tripResult.error === 'CORRUPTED_DATA') {
        setStorageError('Saved trip data was corrupted and has been cleared.');
        safeLocalStorageRemoveItem('currentTrip');
      }
      
      setLoading(false);
    };

    initializeApp();

    // Cleanup function for component unmount
    return () => {
      // Clear any pending save timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        // Force save any pending data
        if (pendingTripRef.current) {
          safeSetObject('currentTrip', pendingTripRef.current);
        }
      }
    };
  }, []);

  const login = (userData: User): void => {
    setUser(userData);
    const result = safeSetObject('user', userData);
    if (!result.success) {
      setStorageError(getErrorMessage(result.error || 'STORAGE_ERROR'));
    }
  };

  const logout = (): void => {
    setUser(null);
    setCurrentTrip(null);
    setStorageError(null);
    safeLocalStorageRemoveItem('user');
    safeLocalStorageRemoveItem('currentTrip');
  };

  // Performance optimization: debounced save function
  const debouncedSaveTrip = useCallback((tripData: Trip) => {
    // Clear any existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Set pending data
    pendingTripRef.current = tripData;
    
    // Schedule save after configurable delay (0ms for tests, 300ms for production)
    const delay = process.env.NODE_ENV === 'test' ? 0 : 300;
    saveTimeoutRef.current = setTimeout(() => {
      if (pendingTripRef.current) {
        const result = safeSetObject('currentTrip', pendingTripRef.current);
        if (!result.success) {
          setStorageError(getErrorMessage(result.error || 'STORAGE_ERROR'));
        }
        
        // Monitor storage usage and warn if near quota
        try {
          const storageStatus = monitorStorageUsage();
          if (storageStatus?.isNearQuota && !storageError) {
            setStorageError(storageStatus.recommendation || 'Storage nearly full');
          }
        } catch (error) {
          // Storage monitoring failed - don't block the save
          console.warn('Storage monitoring failed:', error);
        }
        
        pendingTripRef.current = null;
        saveTimeoutRef.current = null;
      }
    }, delay);
  }, [storageError]);

  const setTrip = (tripData: Trip): void => {
    setCurrentTrip(tripData);
    debouncedSaveTrip(tripData);
  };

  const updateTrip = (updates: Partial<Trip>): void => {
    if (!currentTrip) return;
    
    const updatedTrip = { ...currentTrip, ...updates };
    setCurrentTrip(updatedTrip);
    debouncedSaveTrip(updatedTrip);
  };

  const clearStorageError = (): void => {
    setStorageError(null);
  };

  // For testing: force immediate save of pending data
  const flushPendingSaves = (): void => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      if (pendingTripRef.current) {
        const result = safeSetObject('currentTrip', pendingTripRef.current);
        if (!result.success) {
          setStorageError(getErrorMessage(result.error || 'STORAGE_ERROR'));
        }
        pendingTripRef.current = null;
      }
      saveTimeoutRef.current = null;
    }
  };

  const createNewTrip = async (): Promise<Trip> => {
    if (!user) {
      throw new Error('User must be logged in to create a trip');
    }

    try {
      // TEMPORARY: Create mock trip for testing without API
      const newTrip: Trip = {
        _id: 'test-trip-' + Date.now(),
        userId: user.id,
        destination: '',
        dates: '',
        status: 'planning',
        currentDay: null,
        activities: [],
        conversationSummary: '',
        messages: [],
        createdAt: new Date(),
        updatedAt: new Date()
      };

      setTrip(newTrip);
      return newTrip;
    } catch (error) {
      console.error('Error creating trip:', error);
      throw error;
    }
  };

  const value: UserContextType = {
    user,
    currentTrip,
    loading,
    storageError,
    login,
    logout,
    setTrip,
    updateTrip,
    createNewTrip,
    clearStorageError,
    flushPendingSaves,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};